import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Typography, Button, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, CircularProgress, Alert,
    Menu, MenuItem, ListItemIcon as MuiListItemIcon, Chip,
    TablePagination, TextField, InputAdornment, FormControl, Select
} from '@mui/material';
import { Add, Edit, Delete, LocalOffer, MoreVert, Refresh, Search, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAllCoupons, deleteCoupon } from '../../services/Api';
import DeleteConfirm from '../../modals/DeleteConfirm';

const GetAllCoupons = () => {
    const navigate = useNavigate();
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);

    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [activeFilter, setActiveFilter] = useState('');

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [couponToDelete, setCouponToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Debounce search
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(0);
        }, 800);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    const fetchCoupons = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getAllCoupons(page + 1, rowsPerPage, typeFilter, activeFilter, debouncedSearch);
            // Response could be: { data: [...], total: N } or { success, data: { coupons: [...] } } or array
            const inner = response?.data || response;
            const list = Array.isArray(inner)
                ? inner
                : (Array.isArray(inner?.coupons) ? inner.coupons : (Array.isArray(inner?.data) ? inner.data : []));
            setCoupons(list);
            setTotalRows(
                response?.totalItems || response?.total || response?.totalCount ||
                inner?.total || inner?.totalCount || list.length || 0
            );
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch coupons');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, typeFilter, activeFilter, debouncedSearch]);

    useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

    const handleMenuOpen = (e, coupon) => { setAnchorEl(e.currentTarget); setSelectedCoupon(coupon); };
    const handleMenuClose = () => { setAnchorEl(null); setSelectedCoupon(null); };

    const handleDeleteClick = () => {
        setCouponToDelete(selectedCoupon);
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handleConfirmDelete = async () => {
        try {
            setDeleteLoading(true);
            const response = await deleteCoupon(couponToDelete.id);
            if (response.success) {
                setDeleteDialogOpen(false);
                fetchCoupons();
            } else {
                console.error(response.message || 'Failed to delete coupon');
            }
        } catch (err) {
            console.error('Error deleting coupon:', err);
        } finally {
            setDeleteLoading(false);
        }
    };

    // Format "2026-04-22 05:30:00" or ISO -> "22 Apr 2026"
    const formatDate = (d) => {
        if (!d) return '-';
        const date = new Date(d.replace(' ', 'T'));
        if (isNaN(date)) return d;
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    // Format discount cell
    const formatDiscount = (coupon) => {
        const val = parseFloat(coupon.discountValue) || 0;
        const max = coupon.maxValue ? `₹${parseFloat(coupon.maxValue)}` : null;
        if (coupon.discountType === 'percentage') {
            return { primary: `${val}%`, secondary: max ? `Max ${max}` : 'Percentage' };
        }
        return { primary: `₹${val}`, secondary: 'Fixed' };
    };

    return (
        <Box>
            {/* Header Row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocalOffer sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 300 }}>Coupons</Typography>
                        <Typography variant="body2" color="text.secondary">Manage discount and offer coupons</Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <IconButton onClick={fetchCoupons} disabled={loading} sx={{ border: '1px solid', borderColor: 'divider' }}>
                        <Refresh />
                    </IconButton>
                    <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/coupons/create')} sx={{ borderRadius: '8px' }}>
                        New Coupon
                    </Button>
                </Box>
            </Box>

            {/* Filter Row */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', justifyContent: 'flex-end', mb: 3, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <Select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }} displayEmpty sx={{ borderRadius: '20px', bgcolor: 'rgba(0,0,0,0.02)' }}>
                        <MenuItem value="" disabled>Type</MenuItem>
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="Food">Food</MenuItem>
                        <MenuItem value="Grocery">Grocery</MenuItem>
                        <MenuItem value="Services">Services</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <Select value={activeFilter} onChange={(e) => { setActiveFilter(e.target.value); setPage(0); }} displayEmpty sx={{ borderRadius: '20px', bgcolor: 'rgba(0,0,0,0.02)' }}>
                        <MenuItem value="" disabled>Status</MenuItem>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true">Active</MenuItem>
                        <MenuItem value="false">Inactive</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    placeholder="Search by code..."
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ width: 180, '& .MuiOutlinedInput-root': { borderRadius: '20px', bgcolor: 'rgba(0,0,0,0.02)' } }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
                />
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Discount</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Min Order</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Valid Until</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Usage Limit</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 10 }}><CircularProgress size={40} /></TableCell></TableRow>
                            ) : coupons.length === 0 ? (
                                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 10 }}><Typography color="text.secondary">No coupons found.</Typography></TableCell></TableRow>
                            ) : (
                                coupons.map((coupon, index) => (
                                    <TableRow key={coupon.id} hover>
                                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace', letterSpacing: 1 }}>{coupon.code}</TableCell>
                                        <TableCell>
                                            <Chip label={coupon.type} size="small" sx={{ bgcolor: coupon.type === 'Food' ? 'warning.light' : 'success.light', color: coupon.type === 'Food' ? 'warning.dark' : 'success.dark', fontWeight: 600 }} />
                                        </TableCell>
                                        <TableCell>
                                            {(() => { const { primary, secondary } = formatDiscount(coupon); return (<><span style={{ fontWeight: 600 }}>{primary}</span><Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'capitalize' }}>{secondary}</Typography></>); })()}
                                        </TableCell>
                                        <TableCell>₹{parseFloat(coupon.minOrderAmount || 0).toFixed(2)}</TableCell>
                                        <TableCell>{formatDate(coupon.endDate)}</TableCell>
                                        <TableCell>
                                            {coupon.usedCount ?? 0} / {coupon.usageLimit ?? '∞'}
                                            <Typography variant="caption" color="text.secondary" display="block">used / limit</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={coupon.isActive ? 'Active' : 'Inactive'} size="small"
                                                sx={{ bgcolor: coupon.isActive ? 'success.light' : 'grey.200', color: coupon.isActive ? 'success.dark' : 'grey.700', fontWeight: 600 }} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={(e) => handleMenuOpen(e, coupon)}><MoreVert /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} elevation={1}
                PaperProps={{ sx: { minWidth: 150, border: '1px solid', borderColor: 'text.border', mt: 1 } }}>
                <MenuItem onClick={() => { navigate(`/coupons/details/${selectedCoupon?.id}`); handleMenuClose(); }}>
                    <MuiListItemIcon><Visibility fontSize="small" color="primary" /></MuiListItemIcon>View Details
                </MenuItem>
                <MenuItem onClick={() => { navigate(`/coupons/modify/${selectedCoupon?.id}`, { state: { coupon: selectedCoupon } }); handleMenuClose(); }}>
                    <MuiListItemIcon><Edit fontSize="small" color="primary" /></MuiListItemIcon>Edit
                </MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                    <MuiListItemIcon><Delete fontSize="small" color="error" /></MuiListItemIcon>Delete
                </MenuItem>
            </Menu>

            <DeleteConfirm
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Coupon?"
                message={`Are you sure you want to delete the coupon "${couponToDelete?.code}"? This action cannot be undone.`}
                loading={deleteLoading}
            />
        </Box>
    );
};

export default GetAllCoupons;
